// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (RETUR PASCA-UAT — Slice 2)
// version  : 1.0
// updated  : 2026-08 (Owner: SETUJU UBAH — retur barang cacat)
// protected: NO
// summary  : Daftar "Barang Cacat Menunggu Retur" (IN_RETURN + defect=true, belum masuk
//            dokumen retur aktif). Staff Store memilih beberapa sekaligus -> QR masuk ke
//            form retur (boleh digabung dengan retur biasa). Region-aware (backend scope
//            + RLS), mobile-friendly, loading/empty state.
// =============================================================================
import React, { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, PackageX } from "lucide-react";
import { useV2Auth } from "@/v2/V2AuthContext";

const fmtDate = (s) => {
  if (!s) return "-";
  try { return new Date(s).toLocaleDateString("id-ID", { dateStyle: "medium" }); } catch (e) { return s; }
};

export const DefectWaitingList = ({ store, selected = [], onToggle, refreshKey = 0, testid = "defect-waiting" }) => {
  const { api } = useV2Auth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api("/api/v2/returns/defects-waiting");
      const data = await r.json();
      if (r.ok) {
        const list = (data.items || []).filter((it) => !store || it.store === store);
        setItems(list);
      } else setItems([]);
    } catch (e) { setItems([]); }
    finally { setLoading(false); }
  }, [api, store]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="rounded-xl border border-dashed p-3 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <PackageX className="w-4 h-4 text-destructive" />
          Barang Cacat Menunggu Retur
          <Badge variant="secondary" data-testid={`${testid}-count`}>{items.length}</Badge>
        </div>
        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={load} data-testid={`${testid}-refresh`} title="Muat ulang">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">
        Barang cacat hasil Tukar Barang yang tertahan di store. Centang untuk memasukkan ke dokumen
        retur ini — boleh digabung dengan retur biasa (scan QR di bawah).
      </p>
      {loading && items.length === 0 ? (
        <div className="space-y-2" data-testid={`${testid}-skeleton`}>
          <Skeleton className="h-10 w-full rounded-lg" />
          <Skeleton className="h-10 w-full rounded-lg" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-xs text-muted-foreground text-center py-3" data-testid={`${testid}-empty`}>
          Tidak ada barang cacat menunggu retur.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1" data-testid={`${testid}-list`}>
          {items.map((it) => {
            const checked = selected.includes(it.qr_id);
            return (
              <label key={it.qr_id}
                className={`flex items-start gap-2.5 rounded-lg border p-2 cursor-pointer transition-colors ${checked ? "border-primary bg-primary/5" : "hover:bg-muted/50"}`}
                data-testid={`${testid}-row`}>
                <Checkbox checked={checked} onCheckedChange={() => onToggle(it.qr_id)}
                  className="mt-0.5" data-testid={`${testid}-check-${it.qr_id}`} />
                <span className="flex-1 min-w-0 text-xs space-y-0.5">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <b className="truncate">{it.product_name || "-"}</b>
                    <span className="text-muted-foreground">{it.size || "-"}</span>
                    {it.club && <span className="text-muted-foreground">· {it.club}</span>}
                    <Badge variant="destructive" className="text-[9px] px-1.5 py-0">CACAT</Badge>
                  </span>
                  <span className="block font-mono text-[10px] text-muted-foreground truncate">{it.qr_id}</span>
                  <span className="block text-[10px] text-muted-foreground">
                    Asal: {it.origin_exchange_no || "-"} · {fmtDate(it.origin_at)}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};
